<template>
  <div class="c2c-cat-autocomplete">
    <label for="cat-input">
      {{ t('form.categories_label') }}
      <span class="c2c-cat-optional">{{ t('form.optional') }}</span>
    </label>
    <small>{{ t('form.categories_help') }}</small>

    <div class="c2c-cat-input-row">
      <input
        id="cat-input"
        ref="inputEl"
        v-model="currentInput"
        type="text"
        role="combobox"
        :placeholder="t('form.categories_placeholder')"
        @input="onInput"
        @keydown.enter.prevent="onEnter"
        @keydown.comma.prevent="onEnter"
        @keydown.down.prevent="moveDown"
        @keydown.up.prevent="moveUp"
        @keydown.escape="hideSuggestions"
        @blur="onBlur"
        @focus="onFocus"
        autocomplete="off"
        spellcheck="false"
        :aria-expanded="showSuggestions && suggestions.length > 0"
        aria-autocomplete="list"
        aria-controls="cat-suggestions-list"
        :aria-activedescendant="activeIndex >= 0 ? 'cat-opt-' + activeIndex : undefined"
      />
      <span v-if="loading" class="c2c-cat-loading" aria-hidden="true"></span>

      <ul
        v-if="showSuggestions && suggestions.length > 0"
        id="cat-suggestions-list"
        class="c2c-cat-suggestions"
        role="listbox"
      >
        <li
          v-for="(s, i) in suggestions"
          :key="s"
          :id="'cat-opt-' + i"
          :class="{ 'c2c-cat-suggestion--active': i === activeIndex }"
          @mousedown.prevent="selectSuggestion(s)"
          role="option"
          :aria-selected="i === activeIndex"
        >{{ s }}</li>
      </ul>
    </div>


    <ul v-if="modelValue.length > 0" class="c2c-cat-chips" :aria-label="t('form.categories_selected_aria')">
      <li v-for="(cat, i) in modelValue" :key="cat" class="c2c-cat-chip">
        <span>{{ cat }}</span>
        <button
          type="button"
          class="c2c-cat-chip-remove"
          @click="removeCategory(i)"
          :aria-label="t('form.categories_remove_aria', { name: cat })"
        ><X :size="14" /></button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from "vue"
import { X } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import { searchCategories } from "@/lib/commons-api"

const { t } = useI18n()

const props = defineProps<{ modelValue: string[] }>()
const emit = defineEmits<{ "update:modelValue": [string[]] }>()

const currentInput = ref("")
const suggestions = ref<string[]>([])
const showSuggestions = ref(false)
const activeIndex = ref(-1)
const inputEl = ref<HTMLInputElement | null>(null)
const loading = ref(false)
let debounceTimer: number | undefined

function onInput() {
  clearTimeout(debounceTimer)
  activeIndex.value = -1
  if (currentInput.value.trim().length < 2) {
    suggestions.value = []; showSuggestions.value = false; loading.value = false
    return
  }
  loading.value = true
  debounceTimer = window.setTimeout(async () => {
    const query = currentInput.value.trim()
    try {
      const results = await searchCategories(query)
      suggestions.value = results.filter(s => !props.modelValue.includes(s))
      showSuggestions.value = suggestions.value.length > 0
    } catch { suggestions.value = [] }
    finally { loading.value = false }
  }, 250)
}

function onEnter() {
  const text = currentInput.value.trim().replace(/^Category:/i, "")
  if (!text) return
  if (activeIndex.value >= 0 && activeIndex.value < suggestions.value.length) {
    selectSuggestion(suggestions.value[activeIndex.value])
  } else { addCategory(text) }
}

function moveDown() {
  if (suggestions.value.length === 0) return
  activeIndex.value = Math.min(activeIndex.value + 1, suggestions.value.length - 1)
}
function moveUp() {
  if (suggestions.value.length === 0) return
  activeIndex.value = Math.max(activeIndex.value - 1, -1)
}
function selectSuggestion(s: string) { addCategory(s) }
function addCategory(name: string) {
  const cleaned = name.trim().replace(/^Category:/i, "")
  if (!cleaned || props.modelValue.includes(cleaned) || cleaned.length > 240) return
  emit("update:modelValue", [...props.modelValue, cleaned])
  currentInput.value = ""
  suggestions.value = []
  showSuggestions.value = false
  activeIndex.value = -1
}
function removeCategory(index: number) {
  const next = [...props.modelValue]
  next.splice(index, 1)
  emit("update:modelValue", next)
}
function onBlur() { setTimeout(() => { showSuggestions.value = false }, 150) }
function onFocus() { if (suggestions.value.length > 0) showSuggestions.value = true }
function hideSuggestions() { showSuggestions.value = false; activeIndex.value = -1 }

watch(() => props.modelValue, () => {
  if (suggestions.value.length > 0) {
    suggestions.value = suggestions.value.filter(s => !props.modelValue.includes(s))
  }
})
</script>

<style scoped>
.c2c-cat-autocomplete { display: flex; flex-direction: column; gap: 4px; position: relative; }
.c2c-cat-autocomplete > label { font-weight: 600; font-size: 14px; }
.c2c-cat-optional { font-weight: 400; font-size: 12px; color: var(--color-subtle, #54595d); margin-inline-start: 6px; }
.c2c-cat-autocomplete > small { color: #54595d; font-size: 12px; }
.c2c-cat-input-row { position: relative; }
.c2c-cat-input-row input {
  width: 100%; padding: 8px 32px 8px 10px;
  border: 1px solid #a2a9b1; border-radius: 4px;
  font: inherit; box-sizing: border-box;
}
.c2c-cat-input-row input:focus {
  outline: 2px solid transparent;
  border-color: #3366cc;
  box-shadow: 0 0 0 3px rgba(51, 102, 204, 0.25);
}
.c2c-cat-loading {
  position: absolute; right: 10px; top: 50%;
  width: 14px; height: 14px; margin-top: -7px;
  border: 2px solid #c8ccd1; border-top-color: #3366cc;
  border-radius: 50%; animation: c2c-spin 0.8s linear infinite;
}
@keyframes c2c-spin { to { transform: rotate(360deg); } }
.c2c-cat-suggestions {
  position: absolute; top: 100%; inset-inline: 0;
  margin: 2px 0 0 0; padding: 4px 0; list-style: none;
  background: white; border: 1px solid #a2a9b1; border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  max-height: 240px; overflow-y: auto; z-index: 10;
}
.c2c-cat-suggestions li { padding: 8px 12px; cursor: pointer; font-size: 14px; }
.c2c-cat-suggestions li:hover, .c2c-cat-suggestion--active { background: #eaf3ff; }
.c2c-cat-chips {
  list-style: none; margin: 8px 0 0 0; padding: 0;
  display: flex; flex-wrap: wrap; gap: 6px;
}
.c2c-cat-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 4px 4px 10px;
  background: #eaf3ff; border: 1px solid #c8d6f0;
  border-radius: 16px; font-size: 13px;
}
.c2c-cat-chip-remove {
  background: none; border: none; cursor: pointer;
  color: #54595d;
  width: 22px; height: 22px; padding: 0;
  border-radius: 6px;
  display: inline-flex; align-items: center; justify-content: center;
}
.c2c-cat-chip-remove :deep(svg) { display: block; }
/* Remove is destructive, so hovering it turns red wherever it appears.
   Inside a tinted chip the plain grey hover gave no sign that the
   click would delete anything. */
.c2c-cat-chip-remove:hover { background: #fcd2d2; color: #b32424; }
.c2c-cat-chip-remove:focus-visible { outline: 2px solid #3366cc; outline-offset: 1px; }
</style>
